import React from 'react';
import { isTeacherLoginProfile, isParentLoginProfile } from './teacherModulePermissions';

// Shared with views/dashboard/app/chat.js (the chat sidebar/header already
// render these) so any other list of users - like the add-member picker -
// looks and behaves identically instead of re-deriving its own version.

export const getPresence = (user) => {
    const status = Number(user?.is_online);
    if (status === 1) {
        return { label: 'Online', color: 'text-success' };
    }
    if (status === 2) {
        return { label: 'Away', color: 'text-warning' };
    }
    return { label: 'Offline', color: 'text-secondary' };
};

// Mirrors the role logic used elsewhere in the app (see teacherModulePermissions.js):
// a teacher is any account with the influencer/dance-corner flag set, regardless of
// user_type, so user_type alone can't be mapped 1:1 to a role.
// user_type 2 is admin throughout the codebase (see SessionMiddleware); 4 is the
// default type given to a bare self-registered account and is gated the same way
// as admin everywhere else in the app, so it's grouped with it here too. 5 is
// non-teaching staff (see ProfileController.getConnectedUsers).
export const getUserRole = (user) => {
    if (!user) return '';
    if (isTeacherLoginProfile(user)) return 'Teacher';
    if (isParentLoginProfile(user)) return 'Parent';
    const userType = Number(user?.user_type);
    if (userType === 2 || userType === 4) return 'Admin';
    if (userType === 5) return 'Staff';
    if (userType === 0) return 'Student';
    return '';
};

// Soft-tinted badge colour per role, matching the app's existing bg-soft-* pill styling.
const ROLE_BADGE_COLOR = {
    Teacher: 'primary',
    Student: 'success',
    Admin: 'danger',
    Staff: 'warning',
    Parent: 'info'
};

export const getRoleBadge = (user) => {
    const role = getUserRole(user);
    if (!role) return null;
    return { role, color: ROLE_BADGE_COLOR[role] || 'secondary' };
};

// Small rounded role pill (Teacher/Student/Admin/Staff/Parent) shown under a
// contact's name in the chat sidebar, chat header, and the add-member picker.
export const RoleBadge = ({ user, className = '' }) => {
    const badge = getRoleBadge(user);
    if (!badge) return null;
    return (
        <span
            className={`badge rounded-pill bg-soft-${badge.color} text-${badge.color} ${className}`}
            style={{ fontSize: '10px', fontWeight: 600, padding: '2px 7px', letterSpacing: '0.2px', lineHeight: '1.4' }}
        >
            {badge.role}
        </span>
    );
};

// Small dot + label for online/away/offline, overlaid on an avatar the same
// way the chat sidebar does it (a `position: relative` ancestor with class
// "avatar" is expected, matching Bootstrap's existing .avatar-status rule).
export const PresenceDot = ({ user }) => {
    const presence = getPresence(user);
    return (
        <span className="avatar-status">
            <i className={`material-symbols-outlined ${presence.color} md-14 filled`} title={presence.label}>circle</i>
        </span>
    );
};
